import { Box, Button, Typography, TableCell } from '@mui/material';
import classNames from 'classnames/bind';
import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { ReactComponent as ReportsExcelIcon } from '~/assets/icons/reports-excel.svg';
import { BaseDatePicker } from '~/components/common';
import reportApi, { parseBc01ResponsePayload, type IBc01Params } from '~/apis/report';
import { useAppSelector } from '~/redux/hooks';
import { handleApiError, notify } from '~/utils/helper';
import type { SortDirection } from '~/utils/types/common';
import { SortDirectionEnum } from '~/utils/enum';
import {
  mapJourneyStatusColumnInRows,
  transformBc01HeadCells,
  transformBc01Rows,
  type Bc01TableRow,
} from './components/constants';
import AggregateReportGroupedTable from './components/ReportsTimePeriod';
import BaseTableReport from './components/base-table-report/BaseTableReport';
import { ReportTableMultilineCell } from './components/ReportTableMultilineCell';
import { buildReportExcelFileName, downloadBc01ReportExcel } from './components/excelExport';
import ReportSubjectFilter from './components/ReportSubjectFilter';
import { trackReportFetchEnd, trackReportFetchStart } from './components/reportFetchLoading';
import styles from './Report.module.scss';

dayjs.extend(customParseFormat);

const cx = classNames.bind(styles);

const REPORT_TABLE_CELL_CLASS = 'text-base font-normal text-[#161A1F] text-left align-top whitespace-normal';

function getDefaultReportDateRange(): { start: Date; end: Date } {
  return {
    start: dayjs().startOf('day').toDate(),
    end: dayjs().endOf('day').toDate(),
  };
}

function formatBc01FromTo(startMs: number, endMs: number): { from: string; to: string } {
  const a = dayjs(startMs);
  const b = dayjs(endMs);
  const min = a.isBefore(b, 'day') ? a : b;
  const max = a.isAfter(b, 'day') ? a : b;
  return {
    from: min.startOf('day').format('YYYY-MM-DD HH:mm:ss'),
    to: max.endOf('day').format('YYYY-MM-DD HH:mm:ss'),
  };
}

const BC01_FETCH_DEBOUNCE_MS = 500;

/** Quá thời gian này coi như phản hồi quá chậm / payload quá lớn — gợi ý chỉ chọn 1 đối tượng. */
const BC01_FETCH_TIMEOUT_MS = 90_000;

type ReportFilterState = {
  selectedPersonId: string | null;
  dateRange: { start: Date | null; end: Date | null };
};

function createInitialReportFilterState(): ReportFilterState {
  const d = getDefaultReportDateRange();
  return {
    selectedPersonId: null,
    dateRange: { start: d.start, end: d.end },
  };
}

type ReportFilterAction =
  | { type: 'setSelectedPersonId'; value: string | null }
  | { type: 'clearFilters' }
  | { type: 'setDateRange'; start: Date | null; end: Date | null };

function reportFilterReducer(state: ReportFilterState, action: ReportFilterAction): ReportFilterState {
  switch (action.type) {
    case 'setSelectedPersonId':
      return { ...state, selectedPersonId: action.value };
    case 'clearFilters': {
      const d = getDefaultReportDateRange();
      return {
        ...state,
        selectedPersonId: null,
        dateRange: { start: d.start, end: d.end },
      };
    }
    case 'setDateRange':
      return { ...state, dateRange: { start: action.start, end: action.end } };
    default:
      return state;
  }
}

type Bc01ApiSnapshot = {
  headers: string[];
  data: string[][];
};

const Reports = () => {
  const { t, i18n } = useTranslation('admin/report');
  const { t: tCommon } = useTranslation('admin/common');
  const { selectedUser, userProfile } = useAppSelector((state) => state.auth);
  const username = selectedUser?.username ?? userProfile?.username;

  const [filter, dispatchFilter] = useReducer(reportFilterReducer, undefined, createInitialReportFilterState);
  const { selectedPersonId, dateRange } = filter;

  const [listReady, setListReady] = useState(false);
  const [personsCount, setPersonsCount] = useState(0);

  const [bc01Snapshot, setBc01Snapshot] = useState<Bc01ApiSnapshot>(() => ({ headers: [], data: [] }));
  const [reportLoading, setReportLoading] = useState(false);
  const [aggregateFetchIssue, setAggregateFetchIssue] = useState<'none' | 'timeout'>('none');
  const lastFetchKeyRef = useRef<string>('');
  const reportFetchInFlightRef = useRef(0);

  const hasBc01ColumnsFromApi = bc01Snapshot.headers.length > 0;

  const tableHeaderLabels = useMemo(() => {
    if (hasBc01ColumnsFromApi) return bc01Snapshot.headers;
    return [];
  }, [hasBc01ColumnsFromApi, bc01Snapshot.headers]);

  const transformedColumns = useMemo(() => transformBc01HeadCells(tableHeaderLabels), [tableHeaderLabels]);

  const [sortBy, setSortBy] = useState<string>();
  const [sortOrder, setSortOrder] = useState<SortDirection>(SortDirectionEnum.ASC);

  const transformedRows = useMemo(() => {
    if (!hasBc01ColumnsFromApi) return [];
    return transformBc01Rows(bc01Snapshot.headers, bc01Snapshot.data);
  }, [bc01Snapshot.headers, bc01Snapshot.data, hasBc01ColumnsFromApi]);

  const isMultiDayRange = useMemo(() => {
    const { start, end } = dateRange;
    if (start === null || end === null) return false;
    return !dayjs(start).isSame(dayjs(end), 'day');
  }, [dateRange]);

  const defaultEmptyMessage = !selectedPersonId ? t('filters.selectSubjectToViewReport') : undefined;

  const displayRows = useMemo(() => {
    let rows = transformedRows;
    if (rows.length === 0 && !hasBc01ColumnsFromApi) {
      // Khi chưa chọn đối tượng, hiển thị empty state thay vì row giả.
      rows = defaultEmptyMessage ? [] : [{ id: 'placeholder' } as Bc01TableRow];
    }
    if (!sortBy) return rows;
    const mul = sortOrder === SortDirectionEnum.ASC ? 1 : -1;
    return [...rows].sort((a, b) => {
      const va = String(a[sortBy] ?? '');
      const vb = String(b[sortBy] ?? '');
      return va.localeCompare(vb, undefined, { numeric: true, sensitivity: 'base' }) * mul;
    });
  }, [transformedRows, sortBy, sortOrder, hasBc01ColumnsFromApi, defaultEmptyMessage]);
  const aggregateEmptyMessage =
    aggregateFetchIssue === 'timeout' ? t('dataTooLargeSelectOneSubject') : defaultEmptyMessage;

  const renderAggregateFlatCell = useCallback(
    (row: Bc01TableRow) => (
      <>
        {transformedColumns.map((hc) => (
          <TableCell key={hc.id} className={REPORT_TABLE_CELL_CLASS}>
            <ReportTableMultilineCell text={String(row[hc.id as string] ?? '')} />
          </TableCell>
        ))}
      </>
    ),
    [transformedColumns]
  );

  const reportRangeStartMs = dateRange.start !== null ? dateRange.start.getTime() : null;
  const reportRangeEndMs = dateRange.end !== null ? dateRange.end.getTime() : null;

  const calendarLabels = useMemo(
    () => ({
      buttonLabel: tCommon('calendarPicker.buttonLabel'),
      timeLabel: tCommon('calendarPicker.timeLabel'),
      clickOrDragToSelect: tCommon('calendarPicker.clickOrDragToSelect'),
      dateInputHint: tCommon('calendarPicker.clickOrDragToSelect'),
      invalidDateFormat: tCommon('calendarPicker.invalidDateFormat'),
      startDatePlaceholder: tCommon('calendarPicker.startDate'),
      endDatePlaceholder: tCommon('calendarPicker.endDate'),
      previousMonth: tCommon('calendarPicker.previousMonth'),
      nextMonth: tCommon('calendarPicker.nextMonth'),
      todayNote: tCommon('calendarPicker.todayNote'),
      invalidDateRange: tCommon('calendarPicker.invalidDateRange'),
      endBeforeStart: tCommon('calendarPicker.endBeforeStart'),
      futureDateNotAllowed: tCommon('calendarPicker.futureDateNotAllowed'),
      weekdays: [
        tCommon('calendarPicker.weekdays.mon'),
        tCommon('calendarPicker.weekdays.tue'),
        tCommon('calendarPicker.weekdays.wed'),
        tCommon('calendarPicker.weekdays.thu'),
        tCommon('calendarPicker.weekdays.fri'),
        tCommon('calendarPicker.weekdays.sat'),
        tCommon('calendarPicker.weekdays.sun'),
      ] as [string, string, string, string, string, string, string],
    }),
    [tCommon]
  );

  useEffect(() => {
    if (!username || reportRangeStartMs === null || reportRangeEndMs === null) {
      setBc01Snapshot({ headers: [], data: [] });
      setAggregateFetchIssue('none');
      lastFetchKeyRef.current = '';
      return;
    }

    if (!listReady || personsCount === 0) {
      return;
    }

    if (!selectedPersonId) {
      setBc01Snapshot({ headers: [], data: [] });
      setAggregateFetchIssue('none');
      lastFetchKeyRef.current = '';
      return;
    }

    const personsParam: string[] = [selectedPersonId];
    const nextKey = `${username}|${reportRangeStartMs}|${reportRangeEndMs}|${personsParam.join(',')}`;
    if (nextKey === lastFetchKeyRef.current) {
      return;
    }

    let stale = false;
    let abortController: AbortController | null = null;
    const startMs = reportRangeStartMs;
    const endMs = reportRangeEndMs;

    const timer = window.setTimeout(() => {
      (async () => {
        trackReportFetchStart(reportFetchInFlightRef);
        setReportLoading(true);
        setAggregateFetchIssue('none');
        let fetchTimedOut = false;
        const controller = new AbortController();
        abortController = controller;
        const timeoutId = window.setTimeout(() => {
          fetchTimedOut = true;
          controller.abort();
        }, BC01_FETCH_TIMEOUT_MS);
        try {
          const { from, to } = formatBc01FromTo(startMs, endMs);
          const params: IBc01Params = { username, from, to, persons: personsParam };
          const res = await reportApi.getBc01(params, controller.signal);
          window.clearTimeout(timeoutId);
          if (stale) return;
          const { headers, data: rows } = parseBc01ResponsePayload(res.data);
          setBc01Snapshot({ headers, data: rows });
          lastFetchKeyRef.current = nextKey;
        } catch (e: unknown) {
          window.clearTimeout(timeoutId);
          if (stale) return;
          if (axios.isCancel(e)) {
            if (fetchTimedOut) {
              setBc01Snapshot({ headers: [], data: [] });
              setAggregateFetchIssue('timeout');
            }
            return;
          }
          setAggregateFetchIssue('none');
          setBc01Snapshot({ headers: [], data: [] });
          handleApiError(e);
        } finally {
          trackReportFetchEnd(reportFetchInFlightRef, setReportLoading);
        }
      })();
    }, BC01_FETCH_DEBOUNCE_MS);

    return () => {
      stale = true;
      window.clearTimeout(timer);
      abortController?.abort();
    };
  }, [username, reportRangeStartMs, reportRangeEndMs, selectedPersonId, listReady, personsCount]);

  const handleDateRangeChange = useCallback((start: Date | null, end: Date | null) => {
    dispatchFilter({ type: 'setDateRange', start, end });
  }, []);

  const handleStartDateChange = useCallback(
    (date: string | null) => {
      const start = date ? dayjs(date).toDate() : null;
      handleDateRangeChange(start, dateRange.end);
    },
    [dateRange.end, handleDateRangeChange]
  );

  const handleEndDateChange = useCallback(
    (date: string | null) => {
      const end = date ? dayjs(date).toDate() : null;
      handleDateRangeChange(dateRange.start, end);
    },
    [dateRange.start, handleDateRangeChange]
  );

  const handleClearFilters = useCallback(() => {
    dispatchFilter({ type: 'clearFilters' });
  }, []);

  const handleExport = useCallback(() => {
    const { headers, data } = bc01Snapshot;
    if (data.length === 0) {
      notify('warning', t('exportNoData'));
      return;
    }
    const fileName = buildReportExcelFileName(t('exportFileName'));
    const exportData = mapJourneyStatusColumnInRows(headers, data, i18n.language);
    queueMicrotask(() => {
      downloadBc01ReportExcel(headers, exportData, { fileName });
    });
  }, [bc01Snapshot, t, i18n.language]);

  const handleRequestSort = useCallback((_field: string, _order: SortDirection) => {
    setSortBy(_field);
    setSortOrder(_order);
  }, []);

  return (
    <Box className={cx('report-screen')}>
      <Box
        className='flex justify-between items-center h-20 px-6'
        sx={{
          background: 'var(--Brand-Primary-color_brand_primary, #123B6D)',
        }}
      >
        <Typography className='font-semibold text-xl' sx={{ color: '#fff' }}>
          {t('title')}
        </Typography>
        <Button
          className={cx('export-button')}
          variant='contained'
          disableElevation
          endIcon={<ReportsExcelIcon className={cx('export-button-icon')} width={20} height={20} />}
          onClick={handleExport}
        >
          <span className={cx('export-button-label')}>{t('export')}</span>
        </Button>
      </Box>

      <Box className={cx('report-filter-row')}>
        <ReportSubjectFilter
          username={username}
          triggerId='report-subject-trigger'
          selectedPersonId={selectedPersonId}
          onSelectedPersonIdChange={(value) => dispatchFilter({ type: 'setSelectedPersonId', value })}
          onListReadyChange={(ready, count) => {
            setListReady(ready);
            setPersonsCount(count);
          }}
        />

        <Box className={cx('report-filter-field')}>
          <span className={cx('report-filter-field-label')}>{t('filters.time')}</span>
          <Box className='flex flex-wrap items-center gap-3'>
            <Box sx={{ flex: '1 1 220px', minWidth: 180 }}>
              <BaseDatePicker
                uiVariant='calendarPicker'
                portalId='datePicker-portalId'
                className='w-full'
                placeholder={calendarLabels.startDatePlaceholder}
                value={dateRange.start ? dayjs(dateRange.start).format('YYYY-MM-DD') : undefined}
                onChange={handleStartDateChange}
                dateFormat='YYYY-MM-DD'
                calendarProps={{ popperClassName: styles.reportDatePickerPopper }}
              />
            </Box>
            <Box className='text-[#7C8796]'>-</Box>
            <Box sx={{ flex: '1 1 220px', minWidth: 180 }}>
              <BaseDatePicker
                uiVariant='calendarPicker'
                portalId='datePicker-portalId'
                className='w-full'
                placeholder={calendarLabels.endDatePlaceholder}
                value={dateRange.end ? dayjs(dateRange.end).format('YYYY-MM-DD') : undefined}
                onChange={handleEndDateChange}
                dateFormat='YYYY-MM-DD'
                calendarProps={{ popperClassName: styles.reportDatePickerPopper }}
              />
            </Box>
          </Box>
        </Box>

        <Box className={cx('report-filter-clear')}>
          <span className={cx('report-filter-field-label')} aria-hidden>
            &nbsp;
          </span>
          <Button variant='text' className={cx('clear-filter-button')} onClick={handleClearFilters}>
            {t('filters.clearFilters')}
          </Button>
        </Box>
      </Box>

      <Box className={`px-6 ${cx('report-table-wrap')}`} sx={{ position: 'relative' }}>
        {reportLoading && (
          <Box className={cx('report-table-status-overlay')}>
            <Typography className='text-[#404854]'>{t('tableLoading')}</Typography>
          </Box>
        )}
        {isMultiDayRange ? (
          <AggregateReportGroupedTable
            headCells={transformedColumns}
            rows={displayRows}
            sortBy={sortBy}
            sortOrder={sortOrder}
            onRequestSort={handleRequestSort}
            emptyMessage={aggregateEmptyMessage}
            suppressEmptyState={reportLoading}
          />
        ) : (
          <BaseTableReport<Bc01TableRow>
            scrollable
            hidePagination
            data={displayRows}
            headCells={transformedColumns}
            sortBy={sortBy}
            sortOrder={sortOrder}
            onRequestSort={handleRequestSort}
            emptyMessage={aggregateEmptyMessage}
            suppressEmptyState={reportLoading}
            renderCell={(row) => renderAggregateFlatCell(row)}
          />
        )}
      </Box>
    </Box>
  );
};

export default Reports;
